import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FileText, Download, Calendar, FileSpreadsheet, FileJson, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ExportPanel from './ExportPanel';

export const Reports = () => {
  const [availableReports, setAvailableReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingReport, setDownloadingReport] = useState(null);

  const fetchAvailableReports = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/available`
      );
      const data = await response.json();
      setAvailableReports(data.reports);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load available reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAvailableReports();
  }, [fetchAvailableReports]);

  const handleDownloadReport = async (reportId, format) => {
    setDownloadingReport(`${reportId}-${format}`);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/export/${reportId}?format=${format}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to download report');
      }

      // Get filename from header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${reportId}_${Date.now()}.${format}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`Report downloaded: ${filename}`);
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    } finally {
      setDownloadingReport(null);
    }
  };

  const getFormatIcon = (format) => {
    switch (format) {
      case 'csv':
        return <FileSpreadsheet className="w-4 h-4" />;
      case 'json':
        return <FileJson className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-2">
          <FileText className="w-8 h-8" />
          Reports & Export
        </h1>
        <p className="text-muted-foreground">
          Download comprehensive reports in various formats for analysis and record-keeping
        </p>
      </div>

      {/* Excel Export Panel */}
      <ExportPanel />

      {/* Info Card */}
      <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Report Generation Date: {new Date().toLocaleDateString('en-IN', { 
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                All reports contain the latest data from Salcette Block, Goa
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {availableReports.map((report) => (
          <Card key={report.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="w-5 h-5 text-primary" />
                {report.name}
              </CardTitle>
              <CardDescription className="text-sm">
                {report.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Available Formats */}
              <div>
                <label className="text-sm font-medium mb-2 block">Available Formats:</label>
                <div className="flex flex-wrap gap-2">
                  {report.formats.map((format) => (
                    <Badge key={format} variant="outline" className="text-xs">
                      {format.toUpperCase()}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Download Buttons */}
              <div className="space-y-2">
                {report.formats.map((format) => {
                  const isDownloading = downloadingReport === `${report.id}-${format}`;
                  
                  return (
                    <Button
                      key={format}
                      onClick={() => handleDownloadReport(report.id, format)}
                      disabled={isDownloading}
                      className="w-full"
                      variant={format === 'csv' ? 'default' : 'outline'}
                      size="sm"
                    >
                      {isDownloading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Downloading...
                        </>
                      ) : (
                        <>
                          {getFormatIcon(format)}
                          <span className="ml-2">Download as {format.toUpperCase()}</span>
                        </>
                      )}
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Usage Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>How to Use Reports</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-muted-foreground">
            <div className="flex items-start gap-2">
              <span className="font-bold text-primary">•</span>
              <p>
                <strong className="text-foreground">CSV Format:</strong> Best for importing into Excel, Google Sheets, or data analysis tools.
                Compatible with most spreadsheet applications.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-bold text-primary">•</span>
              <p>
                <strong className="text-foreground">JSON Format:</strong> Ideal for developers and API integrations.
                Machine-readable format for automated processing.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="font-bold text-primary">•</span>
              <p>
                <strong className="text-foreground">Data Freshness:</strong> All reports reflect the current state of the system.
                Historical data is based on the last 6 months of operation.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;